/**
 * Qui valide une demande de paiement fournisseur ou de remboursement d'avoir.
 *
 * Module PUR (ni base ni réseau), importé aussi par le frontend : l'écran
 * « À payer » et le serveur doivent désigner le même valideur pour une ligne.
 *
 * La liste est réglée dans Paiements > Paramètres ; le PREMIER valideur est le défaut.
 * Une ligne mémorise l'adresse choisie à la soumission (`valideur_email`) ;
 * vide, ou portant quelqu'un retiré depuis de la liste, elle revient au
 * défaut — sinon une ligne refusée par un ancien valideur ne pourrait plus
 * être re-soumise, et une ligne soumise n'apparaîtrait chez personne.
 */

export interface Valideur {
  email: string;
  nom: string;
}

/** `mes_lignes` : un valideur NON admin ; `toutes` : tout administrateur. */
export type PorteeValidation = 'mes_lignes' | 'toutes' | 'aucune';

function normaliserEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

/**
 * Lit la valeur stockée. Illisible, vide ou mal formée → `repli` : une liste
 * cassée ne doit pas laisser les demandes sans valideur.
 */
export function lireValideurs(brut: string | null | undefined, repli: Valideur[]): Valideur[] {
  if (!brut) return repli;
  try {
    const lu: unknown = JSON.parse(brut);
    if (!Array.isArray(lu)) return repli;
    const vus = new Set<string>();
    const liste: Valideur[] = [];
    for (const v of lu) {
      const email = normaliserEmail((v as Valideur)?.email);
      const nom = String((v as Valideur)?.nom ?? '').trim();
      if (!email || !nom || vus.has(email)) continue;
      vus.add(email);
      liste.push({ email, nom });
    }
    return liste.length > 0 ? liste : repli;
  } catch {
    return repli;
  }
}

/** `lireValideurs` ne rend jamais une liste vide ; on refuse plutôt que d'inventer un valideur. */
export function valideurParDefaut(liste: Valideur[]): Valideur {
  const defaut = liste[0];
  if (!defaut) throw new Error('Liste des valideurs vide');
  return defaut;
}

export function estDansLaListe(email: string | null | undefined, liste: Valideur[]): boolean {
  const e = normaliserEmail(email);
  return !!e && liste.some((v) => v.email === e);
}

/** L'adresse du valideur qui doit statuer sur une ligne. */
export function valideurEffectif(valideurEmail: string | null | undefined, liste: Valideur[]): string {
  const e = normaliserEmail(valideurEmail);
  return estDansLaListe(e, liste) ? e : valideurParDefaut(liste).email;
}

/** Le prénom affiché ; une adresse inconnue s'affiche telle quelle. */
export function nomDuValideur(email: string | null | undefined, liste: Valideur[]): string {
  const e = normaliserEmail(email);
  return liste.find((v) => v.email === e)?.nom ?? (email ?? '');
}

/**
 * Ce qu'une personne voit dans « À valider ». LE RÔLE ADMIN PRIME SUR LA LISTE
 * (règle de Valérie du 14/10/2026, le soir même) : les trois administrateurs se
 * suppléent, chacun voit et peut traiter les demandes des autres, pour qu'une
 * absence ou une urgence ne bloque pas un paiement. La contrepartie n'est pas
 * négociable — le valideur attribué est prévenu (`valideurDepossede`).
 *
 * Un valideur NON administrateur, lui, reste cantonné à ses demandes : c'est ce
 * qui distingue « les dirigeants se suppléent » de « les paiements sont ouverts
 * à tous ». Ne pas « rétablir » le cloisonnement entre administrateurs : sa
 * disparition est voulue.
 */
export function porteeValidation(
  acteur: { email?: string | null; role?: string | null },
  liste: Valideur[],
): PorteeValidation {
  // LE RÔLE D'ABORD, l'appartenance à la liste ensuite (règle de Valérie du
  // 14/10/2026 : « tout admin doit pouvoir tout voir »).
  //
  // L'ordre inverse avait une conséquence que personne n'avait vue : Valérie
  // doit être DANS la liste pour s'attribuer un paiement et le valider sans
  // prévenir Dominique ni Fabien — les salaires, les factures de direction. Or
  // y entrer lui faisait PERDRE la vue d'ensemble : elle passait d'« admin qui
  // voit tout » à « valideuse qui ne voit que ses lignes ».
  //
  // Un valideur NON admin reste cantonné à ses lignes : rien ne change pour lui.
  if (acteur.role === 'admin') return 'toutes';
  if (estDansLaListe(acteur.email, liste)) return 'mes_lignes';
  return 'aucune';
}

/**
 * Le valideur ATTRIBUÉ à cette ligne, s'il n'est pas l'acteur lui-même.
 *
 * Rend `null` quand l'acteur EST le valideur attribué — il n'y a alors personne
 * à prévenir, et c'est le cas courant : Dominique validant ses propres lignes
 * ne doit déclencher aucun envoi.
 *
 * POURQUOI cette fonction existe (règle de Valérie du 14/10/2026). Valider à la
 * place de quelqu'un se faisait en SILENCE, et le silence porte ici sur de
 * l'argent : le valideur attribué attendrait des factures déjà payées, sans
 * savoir qu'une décision a été prise en son nom.
 *
 * PURE : ni base, ni réseau. Les deux modules (paiements, remboursements)
 * l'appellent, au lieu de recopier la comparaison chacun de son côté.
 */
export function valideurDepossede(
  acteur: { email?: string | null; role?: string | null },
  valideurEmail: string | null | undefined,
  liste: Valideur[],
): string | null {
  const attribue = valideurEffectif(valideurEmail, liste);
  if (!attribue) return null;
  return attribue === normaliserEmail(acteur.email) ? null : attribue;
}

export function peutValider(
  acteur: { email?: string | null; role?: string | null },
  valideurEmail: string | null | undefined,
  liste: Valideur[],
): boolean {
  const portee = porteeValidation(acteur, liste);
  if (portee === 'toutes') return true;
  if (portee === 'aucune') return false;
  return valideurEffectif(valideurEmail, liste) === normaliserEmail(acteur.email);
}
